<template>
  <div v-if="show" class="modal-overlay">
    <div class="modal-content user-form-container">

      <div class="header d-flex justify-content-between align-items-center mb-3">
        <h3 class="m-0">Asignar Concesionaria y Cargos</h3>
        <button class="close-btn" @click="$emit('close')">&times;</button>
      </div>

      <div class="modal-body custom-scroll">
        <div class="form-row">
          <div class="form-group col-12 position-relative">
            <label>Buscar Concesionaria <span class="required">*</span></label>

            <div
              class="d-flex shadow-sm rounded border overflow-hidden bg-white align-items-center search-input-container">
              <div class="pl-3 pr-2 text-muted opacity-50 ml-2">
                <i class="fas fa-search" :class="{ 'fa-spin': isSearching }"></i>
              </div>

              <input type="text" class="form-control border-0 shadow-none px-2" placeholder="Escriba Nombre o CUIT..."
                v-model="searchQuery" @input="handleSearchInput" autocomplete="off"
                @keydown.enter.prevent="handleEnterKey">

              <button v-if="searchQuery" class="btn btn-white text-muted px-3" type="button" @click="clearSearch">
                <i class="fas fa-times-circle"></i>
              </button>
            </div>

            <transition name="fade">
              <div v-if="searchResults.length > 0" class="search-dropdown shadow-lg rounded border-0 mt-1">
                <ul class="list-group list-group-flush">
                  <li v-for="result in searchResults" :key="result.id"
                    
                    class="list-group-item d-flex justify-content-between align-items-center p-3 list-group-item-action cursor-pointer"
                    @click="selectConcessionaire(result)">
                    <div class="d-flex flex-column">
                      <span class="font-weight-bold text-dark">
                        {{ result.business_name }}
                      </span>
                      <small class="text-muted">
                        <i class="fas fa-id-card mr-1"></i> {{ result.cuit }}
                      </small>
                    </div>
                    <i class="fas fa-chevron-right text-muted small"></i>
                  </li>
                </ul>
              </div>
            </transition>

            <div
              v-if="searchQuery && !isSearching && searchResults.length === 0 && searchQuery.length > 3 && !concessionaireData"
              class="mt-2 text-muted small text-center">
              No se encontraron resultados.
            </div>
          </div>
        </div>

        <div v-if="concessionaireData" class="animate-fade-in">
          <div class="form-row">
            <div class="form-group col-12 col-md-6"> <label>Razón Social</label>
              <input type="text" class="form-control" :value="concessionaireData.business_name" disabled />
            </div>

            <div class="form-group col-12 col-md-6"> <label>Sucursal <span class="required">*</span></label>
              <select class="form-control" v-model="selectedSucursalId">
                <option :value="null" disabled>Seleccione...</option>
                <option v-for="suc in availableSucursals" :key="suc.id" :value="suc.id"
                  :disabled="isAlreadyAssigned(suc.id)">
                  {{ suc.cuit }} - {{ suc.business_name }}
                  {{ suc.type === 1 ? '(Casa Central)' : '(Sucursal)' }}
                  {{ isAlreadyAssigned(suc.id) ? '— Ya asignada' : '' }}
                </option>
              </select>
            </div>
          </div>

          <div class="separator-line my-3"></div>
          <div class="form-group">
            <label class="text-primary font-weight-bold">Filtrar Cargos por Área</label>
            <multiselect v-model="selectedAreas" :options="areas" :multiple="true" placeholder="Todas las áreas"
              label="name" track-by="id" />
          </div>

          <div class="form-group mb-0"> <label class="text-primary font-weight-bold">Seleccione Cargos <span
                class="required">*</span></label>
            <multiselect v-model="selectedPositions" :options="filteredPositions" :multiple="true"
              placeholder="Seleccione cargos" label="name" track-by="id">
              <template #option="{ option }">
                <div><strong>{{ option.area?.name }}:</strong> {{ option.name }}</div>
              </template>
            </multiselect>
          </div>
        </div>
      </div>

      <div class="modal-footer mt-3 pt-2 border-top">
        <button class="btn btn-secondary mr-2" @click="$emit('close')">Cancelar</button>
        <button class="btn btn-primary" @click="handleAdd" :disabled="!isValidToAdd">
          {{ buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import Multiselect from "vue-multiselect";
import { checkConcessionaire } from "@/services/api/concessionaireService.js";
import { fetchPositions } from "@/services/api/position";
import { fetchAreas } from "@/services/api/area";
import Swal from "sweetalert2";

import { useConcessionaireSearch } from "@/composables/concessionaire/useConcessionaireSearch";

export default {
  props: {
    show: Boolean,
    assignmentToEdit: Object,
    existingAssignments: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'add-assignment'],
  components: { Multiselect },
  setup(props, { emit }) {
    const localCuit = ref("");
    const isLoading = ref(false);
    const concessionaireData = ref(null);
    const availableSucursals = ref([]);
    const selectedSucursalId = ref(null);

    const areas = ref([]);
    const positions = ref([]);
    const selectedAreas = ref([]);
    const selectedPositions = ref([]);

    const {
      searchQuery,
      searchResults,
      isSearching,
      searchConcessionaires,
      clearSearch: clearSearchBase
    } = useConcessionaireSearch();

    onMounted(async () => {
      const [areasRes, posRes] = await Promise.all([fetchAreas(), fetchPositions()]);
      areas.value = areasRes.data;
      positions.value = posRes;
    });

    const resetSelectionState = () => {
      concessionaireData.value = null;
      selectedSucursalId.value = null;
      availableSucursals.value = [];
      selectedPositions.value = [];
      localCuit.value = "";
    };

    const handleCuitSearch = async () => {
      if (!localCuit.value) return;
      isLoading.value = true;
      concessionaireData.value = null;
      selectedSucursalId.value = null;

      try {
        const result = await checkConcessionaire(localCuit.value);
        if (result?.data) {
          const central = result.data;
          concessionaireData.value = central;
          /*availableSucursals.value = central.sucursals?.length > 0
            ? central.sucursals
            : [central];
           */ 
          const sucursalesExistentes = central.sucursals || [];
      
          availableSucursals.value = [central, ...sucursalesExistentes];
        } else {
          Swal.fire("Sin resultados", "No se encontró la concesionaria", "warning");
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Error", "Error al buscar CUIT", "error");
      } finally {
        isLoading.value = false;
      }
    };

    const handleSearchInput = () => {
      if (concessionaireData.value) resetSelectionState();
      searchConcessionaires({type:1});
    };

    const handleEnterKey = () => {
      if (searchResults.value.length > 0) {
        selectConcessionaire(searchResults.value[0]);
      }
    };

    const clearSearch = () => {
      clearSearchBase();
      resetSelectionState();
    };

    const selectConcessionaire = async (concessionaire) => {
      localCuit.value = concessionaire.cuit;
      searchQuery.value = `${concessionaire.business_name} (${concessionaire.cuit})`;

      searchResults.value = [];

      await handleCuitSearch();
    };

    const filteredPositions = computed(() => {
      if (selectedAreas.value.length === 0) return positions.value;
      const areaIds = selectedAreas.value.map(a => a.id);
      return positions.value.filter(p => areaIds.includes(p.area_id));
    });

    const isValidToAdd = computed(() => {
      return selectedSucursalId.value && selectedPositions.value.length > 0;
    });

    const handleAdd = () => {
      const sucursalObj = availableSucursals.value.find(s => s.id === selectedSucursalId.value);
      const assignment = {
        concessionaire_id: selectedSucursalId.value,
        business_name: sucursalObj.business_name,
        cuit: sucursalObj.cuit,
        positions: JSON.parse(JSON.stringify(selectedPositions.value))
      };

      emit('add-assignment', assignment);

      clearSearch();
      emit('close');
    };

    watch(() => props.show, async (isOpen) => {
      if (isOpen) {
        if (props.assignmentToEdit) {
          isLoading.value = true;

          localCuit.value = props.assignmentToEdit.cuit;
          await handleCuitSearch();

          if (concessionaireData.value) {
            selectedSucursalId.value = props.assignmentToEdit.concessionaire_id;
            searchQuery.value = `${props.assignmentToEdit.business_name} (${props.assignmentToEdit.cuit})`;

            const editingIds = props.assignmentToEdit.positions.map(p => p.id);

            const fullSelectedPositions = positions.value.filter(p => editingIds.includes(p.id));

            selectedPositions.value = fullSelectedPositions;

            const uniqueAreaIds = [...new Set(fullSelectedPositions.map(p => p.area_id))];

            selectedAreas.value = areas.value.filter(a => uniqueAreaIds.includes(a.id));

          }
          isLoading.value = false;
        } else {
          resetSelectionState();
          clearSearch();
        }
      } else {
        clearSearch();
        resetSelectionState();
      }
    });

    const buttonText = computed(() => props.assignmentToEdit ? 'Guardar Cambios' : 'Agregar a la Lista');

    // IDs ya asignados, excluyendo el que se está editando actualmente
    const assignedIds = computed(() => {
      const editingId = props.assignmentToEdit?.concessionaire_id;
      return new Set(
        props.existingAssignments
          .filter(a => a.concessionaire_id !== editingId)
          .map(a => a.concessionaire_id)
      );
    });

    const isAlreadyAssigned = (id) => assignedIds.value.has(id);

    return {
      localCuit, isLoading, concessionaireData, availableSucursals,
      selectedSucursalId, areas, selectedAreas, filteredPositions,
      selectedPositions, isValidToAdd, handleCuitSearch, handleAdd,
      selectConcessionaire, clearSearch, handleSearchInput,
      handleEnterKey,
      isSearching, searchResults, searchQuery, buttonText,
      isAlreadyAssigned
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
}

.modal-content {
  background: white;
  width: 100%;
  max-width: 800px;
  height: auto;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  padding: 0;
}

.header {
  padding: 20px 20px 10px 20px;
  flex-shrink: 0;
}

.modal-body {
  padding: 0 20px;
  overflow-y: auto;
  flex-grow: 1;
  min-height: 400px;
}

.modal-footer {
  padding: 15px 20px;
  flex-shrink: 0;
  background-color: #f8f9fa;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  display: flex;
  justify-content: flex-end;
}

.close-btn {
  font-size: 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  line-height: 1;
  color: #666;
}

.close-btn:hover {
  color: #000;
}

.custom-scroll::-webkit-scrollbar {
  width: 8px;
}

.custom-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.animate-fade-in {
  animation: fadeIn 0.3s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* MEDIA QUERIES PARA NOTEBOOKS Y PANTALLAS CHICAS */
@media (max-height: 800px) {
  .modal-content {
    max-height: 95vh;
  }

  .header {
    padding: 15px 15px 5px 15px;
  }

  .modal-body {
    padding: 0 15px;
  }

  .modal-footer {
    padding: 10px 15px;
  }

  h3 {
    font-size: 1.25rem;
  }
}

.search-input-container {
  border: 1px solid #ced4da !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.search-input-container:focus-within {
  border-color: #80bdff !important;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25) !important;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 1060;
  background: white;
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.15) !important;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}

.cursor-pointer {
  cursor: pointer;
}

.list-group-item-action:hover {
  background-color: #f8f9fa;
}


.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>